import { sanitizeHtml } from '../lib/sanitize';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import { FINANCEMENT_OPTIONS } from '../components/FinancementBadges';
import { CheckCircle2, ExternalLink, Mail } from 'lucide-react';

const SECTIONS = [
  {
    key: 'france-travail',
    eligible: 'Tu es inscrit·e comme demandeur d\'emploi à France Travail (anciennement Pôle Emploi).',
    quick: [
      'AIF (Aide Individuelle à la Formation) : prise en charge totale ou partielle du coût',
      'POEI / POEC si la formation débouche sur un emploi identifié',
      'RFPE (rémunération de formation) durant le stage selon ta situation',
    ],
    body: `
      <p>Si tu es <strong>demandeur d'emploi inscrit à France Travail</strong>, plusieurs dispositifs te permettent
      de financer intégralement notre <strong>formation sellerie</strong> à Istres : l'<strong>AIF</strong>
      (Aide Individuelle à la Formation) est la voie la plus classique — ton conseiller France Travail
      valide la pertinence du projet et le coût pédagogique est pris en charge directement par France Travail.
      Selon ta situation (ARE, ASS, RSA), tu peux aussi bénéficier de la <strong>rémunération de fin de
      formation</strong> ou de la <strong>RFPE</strong> pendant ton stage.</p>
      <p>Étapes à suivre concrètement :</p>
      <ul>
        <li><strong>Réserve une session</strong> sur notre site et télécharge le programme PDF.</li>
        <li>Prends rendez-vous avec ton <strong>conseiller France Travail</strong> et présente-lui le projet.</li>
        <li>Nous t'envoyons un <strong>devis Qualiopi</strong> et la fiche pédagogique conformes aux exigences.</li>
        <li>Ton conseiller valide l'AIF — la formation est financée.</li>
      </ul>
      <p>Notre certification <strong>Qualiopi (n° QUA23050043)</strong> rend nos formations directement éligibles
      à l'AIF. Nous t'accompagnons gratuitement dans le montage du dossier.</p>
    `,
  },
  {
    key: 'fafcea',
    eligible: 'Tu es auto-entrepreneur, artisan immatriculé au Répertoire des Métiers ou chef d\'entreprise artisanale.',
    quick: [
      'Plafond de prise en charge annuel défini par le FAFCEA',
      'Formation directement liée à ton activité artisanale',
      'Dossier à constituer en amont (devis Qualiopi + programme)',
    ],
    body: `
      <p>Le <strong>FAFCEA</strong> (Fonds d'Assurance Formation des Chefs d'Entreprise Artisanale) est l'OPCO
      des <strong>artisans et auto-entrepreneurs</strong> du secteur. Si tu es immatriculé au
      Répertoire des Métiers (RM) ou au Registre National des Entreprises en qualité d'artisan, tu cotises
      automatiquement au FAFCEA via ta CFP (Contribution à la Formation Professionnelle) — ce qui te donne
      droit à un <strong>budget formation annuel</strong>.</p>
      <p>Pour notre <strong>formation sellerie-garnissage</strong>, le FAFCEA prend en charge tout ou partie
      du coût pédagogique, dans la limite du plafond annuel. C'est particulièrement adapté pour les
      <strong>artisans selliers, tapissiers, garnisseurs auto-moto-nautique</strong> qui veulent étoffer
      leur palette technique.</p>
      <p>Étapes :</p>
      <ul>
        <li>Vérifie sur <strong>fafcea.com</strong> que ton code APE/NAF est bien éligible.</li>
        <li>Récupère notre devis Qualiopi + programme + attestation d'inscription.</li>
        <li>Dépose la demande de prise en charge <strong>au moins 30 jours avant</strong> le début du stage.</li>
        <li>Une fois la formation suivie, transmets la facture acquittée + l'attestation de présence pour
            recevoir le remboursement.</li>
      </ul>
      <p>On t'aide à monter le dossier — c'est l'un des financements les plus simples pour les artisans déjà
      installés.</p>
    `,
  },
  {
    key: 'opco-ptp',
    eligible: 'Tu es salarié·e du secteur privé (CDI, CDD, alternance) et tu veux financer une reconversion ou une montée en compétences.',
    quick: [
      'OPCO : financement via ton employeur (plan de développement des compétences)',
      'Projet de Transition Professionnelle (ex-CIF) : si tu changes de métier',
      'Maintien partiel ou total du salaire selon le dispositif',
    ],
    body: `
      <p>Les <strong>OPCO</strong> (Opérateurs de Compétences) financent la formation des salariés via le
      plan de développement des compétences mis en place par l'employeur. Selon ta branche, ton OPCO sera
      <strong>OPCO Mobilités</strong> (transport, automobile, marine), <strong>OPCO Atlas</strong> (services
      financiers), <strong>AFDAS</strong> (culture, médias, sport), <strong>OPCO 2i</strong> (industrie), etc.</p>
      <p>Si ton employeur valide ta formation, l'OPCO peut prendre en charge :</p>
      <ul>
        <li>Le <strong>coût pédagogique</strong> intégral de la formation</li>
        <li>Le <strong>maintien de salaire</strong> partiel ou total durant le stage</li>
        <li>Les <strong>frais annexes</strong> (restauration, transport)</li>
      </ul>
      <p>Si tu veux <strong>changer de métier</strong> sans dépendre de l'accord de ton employeur, le
      <strong>Projet de Transition Professionnelle (PTP, ex-CIF)</strong> géré par <strong>Transitions Pro</strong>
      te permet de te former tout en conservant ton contrat de travail et ta rémunération (sous conditions
      d'ancienneté). C'est le dispositif idéal pour les salariés qui veulent embrasser un nouveau métier
      manuel comme la sellerie.</p>
      <p>Étapes pour l'OPCO classique : informe ton employeur, demande à intégrer la formation dans le plan
      de développement des compétences, l'employeur monte le dossier OPCO, l'OPCO valide et finance.</p>
      <p>Étapes PTP : prends rendez-vous avec Transitions Pro de ta région (en PACA : Transitions Pro PACA),
      dépose ton dossier au moins 4 mois avant. Notre équipe te fournit tous les justificatifs Qualiopi.</p>
    `,
  },
  {
    key: 'agefiph',
    eligible: 'Tu es bénéficiaire de l\'Obligation d\'Emploi des Travailleurs Handicapés (BOETH) — RQTH, AAH, pension d\'invalidité, etc.',
    quick: [
      'Prise en charge intégrale ou partielle de la formation',
      'Aides à l\'aménagement du poste / matériel pédagogique adapté',
      'Cumulable avec France Travail ou un OPCO',
    ],
    body: `
      <p>L'<strong>AGEFIPH</strong> (Association de Gestion du Fonds pour l'Insertion professionnelle des Personnes
      Handicapées) accompagne les <strong>personnes en situation de handicap</strong> dans leur projet de
      formation et d'insertion professionnelle. Si tu bénéficies d'une <strong>RQTH</strong>
      (Reconnaissance de la Qualité de Travailleur Handicapé), de l'<strong>AAH</strong>, d'une pension
      d'invalidité ou d'une rente accident du travail, tu peux bénéficier d'aides AGEFIPH dédiées.</p>
      <p>Pour notre <strong>formation sellerie à Istres</strong>, l'AGEFIPH peut prendre en charge :</p>
      <ul>
        <li>Le <strong>coût pédagogique</strong> de la formation, intégralement ou partiellement</li>
        <li>L'<strong>aménagement du poste pédagogique</strong> ou du matériel (table de coupe adaptée, etc.)</li>
        <li>Le <strong>transport</strong> si la formation est loin de chez toi</li>
        <li>Un <strong>accompagnement personnalisé</strong> par un référent handicap</li>
      </ul>
      <p>Notre atelier d'Istres est <strong>accessible PMR</strong> et notre formateur a déjà accompagné
      plusieurs stagiaires en situation de handicap. L'aide AGEFIPH est cumulable avec un financement
      France Travail (si tu es demandeur d'emploi) ou un OPCO (si tu es salarié).</p>
      <p>Étapes : contacte ton conseiller Cap Emploi ou France Travail référent handicap, dépose ta demande
      AGEFIPH avec notre devis Qualiopi et le programme détaillé. Le dossier est instruit sous 4 à 6 semaines
      en moyenne.</p>
    `,
  },
];

const upsertMeta = (selector, attr, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [_, attrs] = selector.match(/\[(.+)\]/) || [];
    if (attrs) {
      const [k, v] = attrs.split('=');
      el.setAttribute(k, v.replace(/['"]/g, ''));
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};

export const FinancementsPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  useEffect(() => {
    document.title = "Financements de formation — France Travail, FAFCEA, OPCO, AGEFIPH | Sellerie Académie";
    upsertMeta(
      'meta[name="description"]',
      'content',
      "Comment financer ta formation sellerie-garnissage à Istres : France Travail (demandeurs d'emploi), FAFCEA (auto-entrepreneurs et artisans), OPCO et Projet de Transition Pro (salariés), AGEFIPH (handicap). Certification Qualiopi.",
    );
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="financements-page">
      {/* Hero */}
      <section className="bg-[#1F2A4A] text-[#E8DCC8] pt-32 pb-20 lg:pt-40 lg:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C9B896 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
          <p className="text-xs font-mono uppercase tracking-[0.4em] text-[#C9B896] mb-4">FINANCEMENTS</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-[0.95] text-white max-w-4xl">
            Ta formation, <span className="italic text-[#C9B896]">financée.</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed font-light">
            Selon ton statut, plusieurs dispositifs peuvent prendre en charge tout ou partie du coût de
            ta formation sellerie-garnissage à Istres. Nous t'accompagnons gratuitement dans le montage
            de ton dossier.
          </p>
        </div>
      </section>

      {/* Cards row */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 -mt-16 relative z-10 mb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FINANCEMENT_OPTIONS.map((opt) => {
            const Badge = opt.Badge;
            return (
              <a
                key={opt.key}
                href={`#${opt.key}`}
                data-testid={`financement-card-${opt.key}`}
                className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6 hover:border-[#C9B896]/50 hover:shadow-lg transition-all group"
              >
                <div className="mb-4"><Badge size={42} /></div>
                <h3 className="font-serif text-lg text-[#1F2A4A] leading-tight mb-1">{opt.title}</h3>
                <p className="text-xs text-[#1F2A4A]/55 mb-4">{opt.subtitle}</p>
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896] group-hover:text-[#1F2A4A] transition-colors">
                  En savoir plus →
                </span>
              </a>
            );
          })}
        </div>
      </section>

      {/* Detailed sections */}
      <section className="max-w-3xl mx-auto px-6 lg:px-0 pb-24 space-y-24">
        {SECTIONS.map((s) => {
          const meta = FINANCEMENT_OPTIONS.find((o) => o.key === s.key);
          const Badge = meta.Badge;
          return (
            <article key={s.key} id={s.key} className="scroll-mt-32" data-testid={`financement-section-${s.key}`}>
              <div className="flex items-center gap-5 mb-6">
                <Badge size={56} />
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">{meta.subtitle}</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-[#1F2A4A] font-light leading-tight">{meta.title}</h2>
                </div>
              </div>

              <p className="text-sm text-[#1F2A4A]/70 leading-relaxed mb-6 border-l-2 border-[#C9B896] pl-4 italic">
                {s.eligible}
              </p>

              <div className="grid gap-3 mb-7">
                {s.quick.map((q) => (
                  <div key={q} className="flex items-start gap-3 text-sm text-[#1F2A4A]/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-[#C9B896] mt-0.5 shrink-0" />
                    <span>{q}</span>
                  </div>
                ))}
              </div>

              <div
                className="blog-prose text-[#1F2A4A]/85 leading-[1.75]"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.body) }}
              />

              <div className="flex flex-wrap gap-3 mt-8">
                <a
                  href={meta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] transition-colors"
                  data-testid={`financement-official-${s.key}`}
                >
                  <ExternalLink className="w-4 h-4" /> Site officiel
                </a>
                <Link to={L('/contact')}>
                  <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full">
                    <Mail className="w-4 h-4 mr-2" /> Étudier mon dossier
                  </Button>
                </Link>
              </div>
            </article>
          );
        })}
      </section>

      {/* Final CTA */}
      <section className="bg-[#1F2A4A] text-[#E8DCC8] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-[0.4em] text-[#C9B896] mb-4">PRÊT À DÉMARRER ?</p>
          <h3 className="font-serif text-2xl md:text-4xl font-light text-white leading-tight mb-6">
            On t'aide à monter <span className="italic text-[#C9B896]">ton dossier.</span>
          </h3>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Devis Qualiopi, programme détaillé, attestation d'inscription — tout ce qu'il faut pour
            convaincre ton financeur. Réponse sous 48&nbsp;h ouvrées.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to={L('/formations-sur-place')}>
              <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-8 py-6">
                Voir les sessions à Istres
              </Button>
            </Link>
            <Link to={L('/contact')}>
              <Button variant="outline" className="border-[#C9B896]/50 text-[#E8DCC8] hover:bg-[#C9B896]/10 rounded-full px-8 py-6">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancementsPage;
